import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Grid,
    Alert,
    Snackbar,
    Box,
    Chip,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    CloudUpload,
    GitHub,
    Science,
    Sync,
    CheckCircle,
    Error,
    Info,
    Refresh,
    Publish,
    CleaningServices,
    PlayArrow,
    AccountTree
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

// Convert a stored ISO instant (UTC) into the "YYYY-MM-DDTHH:mm" value a
// datetime-local input expects, in the browser's local time. Empty/invalid -> ''.
const isoToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminGitHub() {
    const auth = useAuth();
    const [config, setConfig] = useState({
        repository_name: '',
        access_token: '',
        branch: 'main',
        enabled: false,
        auto_publish: true,
        include_demographics: false,
        notification_email: '',
        publish_cutoff: null
    });
    const [status, setStatus] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [initialized, setInitialized] = useState(false);
    const [cleanupParticipant, setCleanupParticipant] = useState('');
    const [cleanupResult, setCleanupResult] = useState(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [newBranchName, setNewBranchName] = useState('');
    const [createBranchLoading, setCreateBranchLoading] = useState(false);
    const [valPubResult, setValPubResult] = useState(null);
    const [valPubLoading, setValPubLoading] = useState(false);

    const loadGitHubConfig = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('admin/github/config'), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (response.ok) {
                const data = await response.json();
                console.log('Loaded config:', { ...data, access_token: data.access_token ? `${data.access_token.substring(0, 10)}...` : 'empty' });
                // Ensure all fields have proper defaults
                setConfig({
                    repository_name: data.repository_name || '',
                    access_token: data.access_token || '',
                    branch: data.branch || 'main',
                    enabled: data.enabled || false,
                    auto_publish: data.auto_publish || true,
                    include_demographics: data.include_demographics || false,
                    notification_email: data.notification_email || '',
                    publish_cutoff: data.publish_cutoff || null,
                    id: data.id
                });
            } else if (response.status === 404) {
                // No config exists yet - reset to defaults
                console.log('No GitHub configuration found');
                setConfig({
                    repository_name: '',
                    access_token: '',
                    branch: 'main',
                    enabled: false,
                    auto_publish: true,
                    include_demographics: false,
                    notification_email: '',
                    publish_cutoff: null
                });
            } else if (response.status === 401) {
                auth.logout('expired');
            }
        } catch (error) {
            console.error('Error loading GitHub config:', error);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token, auth.logout]);

    const loadGitHubStatus = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('admin/github/status'), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStatus(data);
            } else if (response.status === 401) {
                auth.logout('expired');
            }
        } catch (error) {
            console.error('Error loading GitHub status:', error);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    const loadPublishingSummary = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('admin/github/summary'), {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSummary(data);
            } else if (response.status === 400) {
                // GitHub not enabled
                setSummary(null);
            } else if (response.status === 401) {
                auth.logout('expired');
            } else {
                console.log('Summary endpoint not available:', response.status);
                setSummary(null);
            }
        } catch (error) {
            console.error('Error loading publishing summary:', error);
            setSummary(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token, auth.logout]);

    useEffect(() => {
        const loadData = async () => {
            try {
                await loadGitHubConfig();
                await loadGitHubStatus();
                await loadPublishingSummary();
                setInitialized(true);
            } catch (error) {
                console.error('Error loading GitHub admin data:', error);
                setSnackbar({
                    open: true,
                    message: 'Erro ao carregar dados do GitHub. Verifique a configuração.',
                    severity: 'warning'
                });
                setInitialized(true);
            }
        };
        loadData();
    }, [loadGitHubConfig, loadGitHubStatus, loadPublishingSummary]);

    const handleSaveConfig = async () => {
        setLoading(true);
        console.log('Saving config:', { ...config, access_token: config.access_token ? `${config.access_token.substring(0, 10)}...` : 'empty' });
        try {
            const url = `admin/github/config${config.id ? `/${config.id}` : ''}`;
            const method = config.id ? 'PUT' : 'POST';
            const response = await fetch(buildApiUrl(url), {
                method,
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(config)
            });

            if (response.ok) {
                const data = await response.json();
                setConfig(data);
                setSnackbar({ open: true, message: 'Configuração salva com sucesso!', severity: 'success' });
                loadGitHubStatus();
                loadPublishingSummary();
            } else if (response.status === 401) {
                auth.logout('expired');
            } else {
                const error = await response.json();
                setSnackbar({ open: true, message: `Erro: ${error.detail}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro de conexão: ${error.message}`, severity: 'error' });
        }
        setLoading(false);
    };

    const handleTestConnection = async () => {
        setLoading(true);
        try {
            const response = await fetch(buildApiUrl('admin/github/test'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth.token}`
                }
            });

            const result = await response.json();
            setTestResult(result);
            
            if (result.success) {
                setSnackbar({ open: true, message: 'Conexão testada com sucesso!', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: `Teste falhou: ${result.message}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro de teste: ${error.message}`, severity: 'error' });
        }
        setLoading(false);
    };

    const handlePublishAll = async (missingOnly = false) => {
        setLoading(true);
        try {
            const url = `admin/github/publish-all${missingOnly ? '?missing_only=true' : ''}`;
            const response = await fetch(buildApiUrl(url), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth.token}`
                }
            });

            const result = await response.json();
            
            if (result.success) {
                setSnackbar({ open: true, message: result.message, severity: 'success' });
            } else {
                setSnackbar({ open: true, message: `Publicação falhou: ${result.error}`, severity: 'error' });
            }
            loadGitHubStatus();
            loadPublishingSummary();
        } catch (error) {
            setSnackbar({ open: true, message: `Erro de publicação: ${error.message}`, severity: 'error' });
        }
        setLoading(false);
    };

    const handleUpdateCSV = async () => {
        setLoading(true);
        try {
            const response = await fetch(buildApiUrl('admin/github/update-csv'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth.token}`
                }
            });

            if (response.ok) {
                setSnackbar({ open: true, message: 'CSV atualizado com sucesso!', severity: 'success' });
            } else {
                const error = await response.json();
                setSnackbar({ open: true, message: `Erro: ${error.detail}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro: ${error.message}`, severity: 'error' });
        }
        setLoading(false);
    };

    const handleCleanupDuplicates = async (apply) => {
        if (apply) {
            const confirmed = window.confirm(
                'Aplicar a correção no GitHub?\n\nIsto move os arquivos de validação órfãos para a pasta correta e apaga as pastas duplicadas. Rode a simulação (dry-run) antes para revisar o que será alterado.'
            );
            if (!confirmed) return;
        }
        setCleanupLoading(true);
        try {
            const params = new URLSearchParams();
            if (apply) params.set('apply', 'true');
            if (cleanupParticipant.trim()) params.set('participant', cleanupParticipant.trim());
            const qs = params.toString();
            const response = await fetch(
                buildApiUrl(`admin/github/cleanup-duplicates${qs ? `?${qs}` : ''}`),
                { method: 'POST', headers: { 'Authorization': `Bearer ${auth.token}` } }
            );
            const result = await response.json();

            if (response.ok) {
                setCleanupResult(result);
                const changes = (result.total_moves || 0) + (result.total_deletes || 0);
                let message;
                if (apply) {
                    message = `Correção aplicada: ${result.total_moves} movimentação(ões), ${result.total_deletes} exclusão(ões).`;
                } else if (changes > 0) {
                    message = `Simulação: ${result.total_moves} movimentação(ões) e ${result.total_deletes} exclusão(ões) em ${result.participants_with_changes} participante(s). Revise abaixo e clique em Aplicar.`;
                } else {
                    message = 'Simulação: nenhuma pasta duplicada encontrada.';
                }
                setSnackbar({
                    open: true,
                    message,
                    severity: apply ? 'success' : (changes > 0 ? 'info' : 'success')
                });
                if (apply) {
                    loadGitHubStatus();
                    loadPublishingSummary();
                }
            } else if (response.status === 401) {
                auth.logout('expired');
            } else {
                setSnackbar({ open: true, message: `Erro: ${result.detail || 'falha na operação'}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro: ${error.message}`, severity: 'error' });
        }
        setCleanupLoading(false);
    };

    const handleCreateBranch = async () => {
        const name = newBranchName.trim();
        if (!name) {
            setSnackbar({ open: true, message: 'Informe o nome da nova branch.', severity: 'warning' });
            return;
        }
        setCreateBranchLoading(true);
        try {
            const response = await fetch(
                buildApiUrl(`admin/github/create-branch?branch=${encodeURIComponent(name)}`),
                { method: 'POST', headers: { 'Authorization': `Bearer ${auth.token}` } }
            );
            const result = await response.json();
            if (response.ok) {
                // Prefill the config branch so the user just clicks "Salvar Configuração".
                setConfig((prev) => ({ ...prev, branch: name }));
                setSnackbar({
                    open: true,
                    message: `Branch '${name}' criada (vazia). Ela foi preenchida no campo Branch — clique em "Salvar Configuração" para publicar nela.`,
                    severity: 'success'
                });
            } else if (response.status === 401) {
                auth.logout('expired');
            } else {
                setSnackbar({ open: true, message: `Erro: ${result.detail || 'falha ao criar branch'}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro: ${error.message}`, severity: 'error' });
        }
        setCreateBranchLoading(false);
    };

    const handlePublishValidations = async (apply) => {
        if (apply) {
            const confirmed = window.confirm(
                'Publicar todas as validações na branch configurada?\n\nRode isto DEPOIS de "Publicar Todas" (para as pastas de trial já existirem). Rode a simulação antes para revisar.'
            );
            if (!confirmed) return;
        }
        setValPubLoading(true);
        try {
            const response = await fetch(
                buildApiUrl(`admin/github/publish-validations${apply ? '?apply=true' : ''}`),
                { method: 'POST', headers: { 'Authorization': `Bearer ${auth.token}` } }
            );
            const result = await response.json();
            if (response.ok) {
                setValPubResult(result);
                const fallbacks = result.would_create_fallback?.length || 0;
                let message;
                if (apply) {
                    message = `Validações publicadas: ${result.published} de ${result.total_candidates}.`;
                } else {
                    message = `Simulação: ${result.total_candidates} validação(ões) candidata(s)`
                        + (fallbacks ? ` — ⚠️ ${fallbacks} sem pasta de trial (rode "Publicar Todas" antes).` : '.');
                }
                setSnackbar({
                    open: true,
                    message,
                    severity: apply ? 'success' : (fallbacks ? 'warning' : 'info')
                });
                if (apply) { loadGitHubStatus(); }
            } else if (response.status === 401) {
                auth.logout('expired');
            } else {
                setSnackbar({ open: true, message: `Erro: ${result.detail || 'falha na operação'}`, severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: `Erro: ${error.message}`, severity: 'error' });
        }
        setValPubLoading(false);
    };

    const getStatusIcon = (syncStatus) => {
        switch (syncStatus) {
            case 'success': return <CheckCircle color="success" />;
            case 'failed': return <Error color="error" />;
            case 'in_progress': return <CircularProgress size={20} />;
            default: return <Info color="info" />;
        }
    };

    const getStatusColor = (syncStatus) => {
        switch (syncStatus) {
            case 'success': return 'success';
            case 'failed': return 'error';
            case 'in_progress': return 'warning';
            default: return 'default';
        }
    };

    if (!initialized) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                <GitHub sx={{ mr: 1, verticalAlign: 'middle' }} />
                Configuração GitHub
            </Typography>

            {/* Status Card */}
            {status && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Status da Publicação</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">Estado:</Typography>
                                    <Chip
                                        icon={status.enabled ? <CheckCircle /> : <Error />}
                                        label={status.enabled ? 'Ativo' : 'Inativo'}
                                        color={status.enabled ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">Auto-publicar:</Typography>
                                    <Chip
                                        label={status.auto_publish ? 'Sim' : 'Não'}
                                        color={status.auto_publish ? 'primary' : 'default'}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="body2">
                                    Submissions confirmadas{status.publish_cutoff ? ' (após corte)' : ''}: {status.total_confirmed_submissions}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {getStatusIcon(status.last_sync_status)}
                                    <Typography variant="body2">
                                        Último sync: {status.last_sync_status || 'Nunca'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        {status.publish_cutoff && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Data de corte ativa: experimentos anteriores a{' '}
                                <b>{new Date(status.publish_cutoff).toLocaleString()}</b> são ignorados na publicação.
                            </Alert>
                        )}
                        {status.last_sync_message && (
                            <Alert severity={getStatusColor(status.last_sync_status)} sx={{ mt: 2 }}>
                                {status.last_sync_message}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Publishing Summary Card */}
            {summary && summary.success && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Resumo de Publicações</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="primary">
                                        {summary.total_submissions}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total de Submissions
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="success.main">
                                        {summary.existing_in_github}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Já Publicadas
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box textAlign="center">
                                    <Typography variant="h4" color="warning.main">
                                        {summary.missing_from_github}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Pendentes
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        {summary.missing_from_github > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Há {summary.missing_from_github} submissions que ainda não foram publicadas no GitHub.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Configuration Card */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Configuração do Repositório</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nome do Repositório"
                                placeholder="owner/repo"
                                value={config.repository_name}
                                onChange={(e) => setConfig({ ...config, repository_name: e.target.value })}
                                helperText="Formato: dono/repositorio (ex: researcher/data-repo)"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Branch"
                                value={config.branch}
                                onChange={(e) => setConfig({ ...config, branch: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Token de Acesso GitHub"
                                type="password"
                                value={config.access_token || ''}
                                onChange={(e) => setConfig({ ...config, access_token: e.target.value })}
                                placeholder={config.access_token ? '••••••••••••••••••••••••••••••••••••••••' : 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                                helperText="Personal Access Token com permissões de escrita no repositório"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email para Notificações"
                                type="email"
                                value={config.notification_email}
                                onChange={(e) => setConfig({ ...config, notification_email: e.target.value })}
                                helperText="Opcional: receber notificações de sync"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Data de corte (opcional)"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={isoToLocalInput(config.publish_cutoff)}
                                onChange={(e) => setConfig({
                                    ...config,
                                    publish_cutoff: e.target.value ? new Date(e.target.value).toISOString() : null
                                })}
                                helperText="Experimentos anteriores a esta data/hora são ignorados na publicação (deixe vazio = todos)"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.enabled}
                                        onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                                    />
                                }
                                label="Habilitar publicação no GitHub"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.auto_publish}
                                        onChange={(e) => setConfig({ ...config, auto_publish: e.target.checked })}
                                    />
                                }
                                label="Publicar automaticamente ao confirmar resposta"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.include_demographics}
                                        onChange={(e) => setConfig({ ...config, include_demographics: e.target.checked })}
                                    />
                                }
                                label="Incluir dados demográficos dos participantes"
                            />
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveConfig}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
                        >
                            Salvar Configuração
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleTestConnection}
                            disabled={loading || !config.repository_name?.trim() || !config.access_token?.trim()}
                            startIcon={<Science />}
                        >
                            Testar Conexão
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Test Result */}
            {testResult && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Resultado do Teste</Typography>
                        <Alert severity={testResult.success ? 'success' : 'error'}>
                            {testResult.message}
                        </Alert>
                        {testResult.repository_info && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Repositório: {testResult.repository_info.name} (branch: {testResult.repository_info.branch})
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Actions Card */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Ações</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => handlePublishAll(false)}
                                disabled={!status?.enabled || loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <Publish />}
                            >
                                Publicar Todas
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Tooltip title={summary?.missing_from_github ? `Publicar apenas as ${summary.missing_from_github} submissions que ainda não estão no GitHub` : "Nenhuma submission pendente"}>
                                <span>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => handlePublishAll(true)}
                                        disabled={!status?.enabled || loading || !summary?.missing_from_github}
                                        startIcon={loading ? <CircularProgress size={20} /> : <Publish />}
                                        color="warning"
                                    >
                                        Publicar Pendentes
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleUpdateCSV}
                                disabled={!status?.enabled || loading}
                                startIcon={<Sync />}
                            >
                                Atualizar CSV
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    loadGitHubStatus();
                                    loadPublishingSummary();
                                }}
                                startIcon={<Refresh />}
                            >
                                Atualizar Status
                            </Button>
                        </Grid>
                    </Grid>
                    {summary && summary.missing_from_github === 0 && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            Todas as submissions estão publicadas no GitHub!
                        </Alert>
                    )}
                </CardContent>
            </Card>


            {/* Cleanup Duplicate Folders Card */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        <CleaningServices sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Sanear Pastas Duplicadas
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Localiza trials duplicados no GitHub (pastas com o mesmo <code>_Target_</code> criadas por falhas
                        antigas de validação), move o arquivo de validação órfão para a pasta correta e remove a duplicata.
                        Rode a simulação (dry-run) primeiro para revisar o que será alterado.
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Participante (opcional)"
                                placeholder="ex.: TB"
                                value={cleanupParticipant}
                                onChange={(e) => setCleanupParticipant(e.target.value)}
                                helperText="Deixe em branco para varrer todos os participantes"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={8}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => handleCleanupDuplicates(false)}
                                    disabled={!status?.enabled || cleanupLoading}
                                    startIcon={cleanupLoading ? <CircularProgress size={20} /> : <Science />}
                                >
                                    Simular (dry-run)
                                </Button>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={() => handleCleanupDuplicates(true)}
                                    disabled={
                                        !status?.enabled || cleanupLoading ||
                                        !cleanupResult || !cleanupResult.dry_run ||
                                        ((cleanupResult.total_moves || 0) + (cleanupResult.total_deletes || 0)) === 0
                                    }
                                    startIcon={<PlayArrow />}
                                >
                                    Aplicar Correção
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    {cleanupResult && (
                        <Box sx={{ mt: 3 }}>
                            <Alert
                                severity={
                                    cleanupResult.failures?.length ? 'warning'
                                        : (((cleanupResult.total_moves || 0) + (cleanupResult.total_deletes || 0)) > 0
                                            ? (cleanupResult.dry_run ? 'info' : 'success') : 'success')
                                }
                                sx={{ mb: 2 }}
                            >
                                {cleanupResult.dry_run ? 'Simulação (nada foi alterado)' : 'Correção aplicada'}
                                {' — '}verificados: {cleanupResult.participants_scanned},
                                {' '}com alterações: {cleanupResult.participants_with_changes},
                                {' '}mover: {cleanupResult.total_moves}, apagar: {cleanupResult.total_deletes}
                                {cleanupResult.failures?.length ? `, falhas: ${cleanupResult.failures.join(', ')}` : ''}
                            </Alert>

                            {cleanupResult.details?.length > 0 ? cleanupResult.details.map((d) => (
                                <Box key={d.participant} sx={{ mb: 2, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2">{d.participant}</Typography>
                                    {d.notes?.map((n, i) => (
                                        <Typography key={`note${i}`} variant="caption" display="block" color="text.secondary">
                                            {n}
                                        </Typography>
                                    ))}
                                    {d.moves?.map((m, i) => (
                                        <Typography key={`mv${i}`} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                            MOVER: {m.from} → {m.to}
                                        </Typography>
                                    ))}
                                    {d.deletes?.map((p, i) => (
                                        <Typography key={`del${i}`} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                                            APAGAR: {p}
                                        </Typography>
                                    ))}
                                </Box>
                            )) : (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhuma pasta duplicada encontrada.
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* New-format publishing branch migration */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        <AccountTree sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Migrar para nova branch (formato novo)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Reescreve todos os trials no novo padrão (<code>{'{PID}_{data}_{HH-MM}_Target_{env}'}</code>)
                        numa branch limpa. Passos: <b>1)</b> crie a branch vazia, <b>2)</b> salve a config apontando
                        para ela, <b>3)</b> use "Publicar Todas" (acima), <b>4)</b> publique as validações abaixo.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>1. Criar branch de publicação (órfã/vazia)</Typography>
                    <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={5}>
                            <TextField
                                fullWidth
                                label="Nome da nova branch"
                                placeholder="ex.: main-v2"
                                value={newBranchName}
                                onChange={(e) => setNewBranchName(e.target.value)}
                                helperText="Branch nova, sem pastas de trial. Falha se já existir."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={7}>
                            <Button
                                variant="outlined"
                                onClick={handleCreateBranch}
                                disabled={!status?.enabled || createBranchLoading || !newBranchName.trim()}
                                startIcon={createBranchLoading ? <CircularProgress size={20} /> : <AccountTree />}
                            >
                                Criar branch (vazia)
                            </Button>
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        4. Publicar validações na branch configurada ({status?.branch || config.branch})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        "Publicar Todas" (acima) grava os trials, mas não os <code>validation.txt</code>. Rode isto
                        <b> depois</b> para completar. A simulação avisa se alguma validação não achou a pasta do trial.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => handlePublishValidations(false)}
                            disabled={!status?.enabled || valPubLoading}
                            startIcon={valPubLoading ? <CircularProgress size={20} /> : <Science />}
                        >
                            Simular validações
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handlePublishValidations(true)}
                            disabled={
                                !status?.enabled || valPubLoading ||
                                !valPubResult || !valPubResult.dry_run ||
                                (valPubResult.total_candidates || 0) === 0
                            }
                            startIcon={<Publish />}
                        >
                            Publicar Validações
                        </Button>
                    </Box>

                    {valPubResult && (
                        <Box sx={{ mt: 2 }}>
                            <Alert
                                severity={
                                    valPubResult.failures?.length ? 'error'
                                        : (valPubResult.would_create_fallback?.length && valPubResult.dry_run ? 'warning' : 'info')
                                }
                            >
                                {valPubResult.dry_run ? 'Simulação' : 'Publicado'} —
                                {' '}candidatas: {valPubResult.total_candidates},
                                {' '}{valPubResult.dry_run ? 'sem pasta de trial' : 'publicadas'}: {valPubResult.dry_run ? (valPubResult.would_create_fallback?.length || 0) : valPubResult.published}
                                {valPubResult.failures?.length ? `, falhas: ${valPubResult.failures.length}` : ''}
                            </Alert>
                            {valPubResult.dry_run && valPubResult.would_create_fallback?.length > 0 && (
                                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                                    ⚠️ {valPubResult.would_create_fallback.length} validação(ões) sem pasta de trial correspondente —
                                    rode "Publicar Todas" antes, senão elas criarão pastas órfãs só com validation.txt.
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>


            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}